import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ExternalLink, MessageCircle, MessagesSquare, Plus, Search, Sparkles, UserMinus, Undo2 } from 'lucide-react';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import CommunityVideoEditor, { CommunityVideo } from '../../components/Admin/CommunityVideoEditor';
import { COMMUNITY_TERMS_TEMPLATE_PT } from '../../components/Admin/communityTermsTemplate';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { ActionButton, Button, IconButton, RowActions, Tabs } from '../../components/UI';

type Lang = 'pt' | 'en' | 'es';
type Currency = 'EUR' | 'BRL';
type CurrencyMode = 'both' | Currency;
type PixSettings = { enabled: boolean; iofPaidBy: 'customer' | 'office' };
const offeredCurrencies = (mode?: CurrencyMode): Currency[] =>
  mode === 'EUR' || mode === 'BRL' ? [mode] : ['EUR', 'BRL'];
type MemberStatus = 'pending' | 'active' | 'past_due' | 'canceled' | 'expired';
type MemberFilter = 'active' | 'notJoined' | 'toRemove' | 'pastDue' | 'ended' | 'pending' | '';

interface Community {
  _id: string;
  name: string;
  slug: string;
  i18n: Record<Lang, { title: string; description: string; terms?: string; video?: CommunityVideo }>;
  whatsappGroupUrl: string;
  enabled: boolean;
  showCompanyDoc?: boolean;
  interval: 'month' | 'year';
  currencyMode?: CurrencyMode;
  pix?: PixSettings;
  prices: Record<Currency, { amount: number; stripePriceId: string }>;
  offerUrl: string;
  counts?: { active: number; toRemove: number; total: number };
}

interface Member {
  _id: string;
  email: string;
  name: string;
  currency: Currency;
  status: MemberStatus;
  paidUntil?: string | null;
  activatedAt?: string | null;
  stripeCustomerId?: string;
  groupJoin?: { clickedAt?: string | null; clicks?: number };
  removalPendingAt?: string | null;
  removedAt?: string | null;
  refundedAt?: string | null;
  termsAcceptedAt?: string | null;
  quizLead?: { name: string; whatsapp: string } | null;
  createdAt: string;
}

const LANGS: { key: Lang; label: string }[] = [
  { key: 'pt', label: 'Português' },
  { key: 'en', label: 'English' },
  { key: 'es', label: 'Español' }
];

const STATUS_BADGE: Record<MemberStatus, { label: string; cls: string }> = {
  pending: { label: 'Aguardando pagamento', cls: 'bg-gray-100 text-gray-700' },
  active: { label: 'Ativo', cls: 'bg-green-100 text-green-800' },
  past_due: { label: 'Pagamento falhou', cls: 'bg-amber-100 text-amber-800' },
  canceled: { label: 'Cancelado', cls: 'bg-red-100 text-red-700' },
  expired: { label: 'Expirado', cls: 'bg-red-100 text-red-700' }
};

const FILTERS: { key: MemberFilter; label: string }[] = [
  { key: 'active', label: 'Ativos' },
  { key: 'notJoined', label: 'Não entraram no grupo' },
  { key: 'toRemove', label: 'A remover do grupo' },
  { key: 'pastDue', label: 'Pagamento falhou' },
  { key: 'ended', label: 'Cancelados/expirados' },
  { key: 'pending', label: 'Não concluíram' },
  { key: '', label: 'Todos' }
];

const emptyForm = () => ({
  name: '',
  slug: '',
  whatsappGroupUrl: '',
  enabled: true,
  showCompanyDoc: true,
  interval: 'month' as 'month' | 'year',
  currencyMode: 'both' as CurrencyMode,
  pix: { enabled: false, iofPaidBy: 'customer' } as PixSettings,
  prices: { EUR: { amount: '' as number | string }, BRL: { amount: '' as number | string } },
  i18n: {
    pt: { title: '', description: '', terms: '' },
    en: { title: '', description: '', terms: '' },
    es: { title: '', description: '', terms: '' }
  } as Record<Lang, { title: string; description: string; terms: string }>
});

type FormState = ReturnType<typeof emptyForm>;

const formatDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');
const formatMoney = (value: number, currency: Currency) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
const formatPhone = (raw: string) => parsePhoneNumberFromString(raw)?.formatInternational() || raw;

const AdminCommunities: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotifications();
  const confirm = useConfirm();

  const [communities, setCommunities] = useState<Community[]>([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formLang, setFormLang] = useState<Lang>('pt');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const handleUseTermsTemplate = async () => {
    if (form.i18n.pt.terms.trim()) {
      const ok = await confirm({ message: 'Substituir o termo em português pelo modelo padrão?', confirmText: 'Substituir' });
      if (!ok) return;
    }
    setForm((prev) => ({ ...prev, i18n: { ...prev.i18n, pt: { ...prev.i18n.pt, terms: COMMUNITY_TERMS_TEMPLATE_PT } } }));
  };

  // Mesmo "Traduzir com IA" dos outros editores: gera en e es a partir do pt (o admin revisa e salva).
  const handleTranslate = async () => {
    const pt = form.i18n.pt;
    try {
      setTranslating(true);
      const res = await axios.post('/community/admin/translate', {
        title: pt.title || form.name,
        description: pt.description,
        terms: pt.terms
      });
      const { en, es } = res.data;
      setForm((prev) => ({
        ...prev,
        i18n: { ...prev.i18n, en: { ...prev.i18n.en, ...en }, es: { ...prev.i18n.es, ...es } }
      }));
      showSuccess('Tradução gerada!', 'Revise título, descrição e termo em inglês e espanhol antes de salvar.');
    } catch (err: any) {
      showError('Erro ao traduzir', err.response?.data?.message || 'Tente novamente.');
    } finally {
      setTranslating(false);
    }
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<MemberFilter>('active');
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);

  const fetchCommunities = useCallback(async () => {
    try {
      const res = await axios.get('/community/admin');
      setCommunities(res.data.communities);
      setConfigured(res.data.configured);
      setSelectedId((prev) => prev || res.data.communities[0]?._id || null);
    } catch (error) {
      console.error('Erro ao carregar comunidades:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    if (!selectedId) return;
    try {
      setMembersLoading(true);
      const params = new URLSearchParams({ limit: '100', ...(filter && { filter }), ...(search && { search }) });
      const res = await axios.get(`/community/admin/${selectedId}/members?${params}`);
      setMembers(res.data.members);
      setMembersTotal(res.data.total);
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
    } finally {
      setMembersLoading(false);
    }
  }, [selectedId, filter, search]);

  useEffect(() => { fetchCommunities(); }, [fetchCommunities]);
  useEffect(() => {
    const timer = setTimeout(fetchMembers, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchMembers, search]);

  const openForm = (community?: Community) => {
    if (community) {
      setForm({
        name: community.name,
        slug: community.slug,
        whatsappGroupUrl: community.whatsappGroupUrl,
        enabled: community.enabled,
        showCompanyDoc: community.showCompanyDoc !== false,
        interval: community.interval,
        currencyMode: community.currencyMode || 'both',
        pix: { enabled: !!community.pix?.enabled, iofPaidBy: community.pix?.iofPaidBy || 'customer' },
        prices: {
          EUR: { amount: community.prices.EUR?.amount || '' },
          BRL: { amount: community.prices.BRL?.amount || '' }
        },
        i18n: {
          pt: { title: community.i18n?.pt?.title || '', description: community.i18n?.pt?.description || '', terms: community.i18n?.pt?.terms || '' },
          en: { title: community.i18n?.en?.title || '', description: community.i18n?.en?.description || '', terms: community.i18n?.en?.terms || '' },
          es: { title: community.i18n?.es?.title || '', description: community.i18n?.es?.description || '', terms: community.i18n?.es?.terms || '' }
        }
      });
      setEditingId(community._id);
    } else {
      setForm(emptyForm());
      setEditingId('new');
    }
    setFormLang('pt');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        prices: {
          EUR: { amount: Number(form.prices.EUR.amount) || 0 },
          BRL: { amount: Number(form.prices.BRL.amount) || 0 }
        }
      };
      const res = editingId === 'new'
        ? await axios.post('/community/admin', payload)
        : await axios.put(`/community/admin/${editingId}`, payload);
      showSuccess('Comunidade salva', 'Preços sincronizados com a Stripe.');
      setEditingId(null);
      await fetchCommunities();
      setSelectedId(res.data._id);
    } catch (error: any) {
      showError('Erro ao salvar', error.response?.data?.message || 'Não foi possível salvar a comunidade');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (community: Community) => {
    const ok = await confirm({ message: `Excluir a comunidade "${community.name}"?`, confirmText: 'Excluir' });
    if (!ok) return;
    try {
      await axios.delete(`/community/admin/${community._id}`);
      if (selectedId === community._id) setSelectedId(null);
      await fetchCommunities();
    } catch (error: any) {
      showError('Erro ao excluir', error.response?.data?.message || 'Não foi possível excluir');
    }
  };


  const toggleRemoved = async (member: Member) => {
    try {
      const removed = !member.removedAt;
      const res = await axios.put(`/community/admin/members/${member._id}/removed`, { removed });
      setMembers((prev) => prev.map((m) => (m._id === member._id ? { ...m, removedAt: res.data.removedAt } : m)));
      fetchCommunities();
    } catch (error: any) {
      showError('Erro', error.response?.data?.message || 'Não foi possível atualizar o membro');
    }
  };

  const openPortal = async (member: Member) => {
    try {
      const res = await axios.post(`/community/admin/members/${member._id}/portal`);
      window.open(res.data.url, '_blank', 'noopener');
    } catch (error: any) {
      showError('Erro ao abrir o portal', error.response?.data?.message || 'Ative o Customer Portal no Dashboard da Stripe');
    }
  };

  const selected = communities.find((c) => c._id === selectedId) || null;
  const inputCls = 'field';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs items={[{ label: t('breadcrumbs.dashboard'), href: '/admin' }, { label: t('admin.communities') }]} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.communities')}</h1>
              <p className="text-gray-600">{t('admin.communitiesDesc')}</p>
            </div>
            <Button type="button" onClick={() => openForm()} disabled={!configured}>
              <Plus className="w-4 h-4" /> Nova comunidade
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        {!configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
            As chaves <code>COMMUNITY_STRIPE_*</code> não estão no <code>.env</code> do servidor. Sem elas não dá para criar ou editar comunidades.
          </div>
        )}

        {editingId && (
          <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{editingId === 'new' ? 'Nova comunidade' : 'Editar comunidade'}</h2>
              <IconButton action="close" onClick={() => setEditingId(null)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome interno</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug (endereço /comunidade/…)</label>
                <input value={form.slug} placeholder="gerado a partir do nome" onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Link do grupo do WhatsApp</label>
                <input
                  type="url"
                  value={form.whatsappGroupUrl}
                  placeholder="https://chat.whatsapp.com/…"
                  onChange={(e) => setForm({ ...form, whatsappGroupUrl: e.target.value })}
                  className={inputCls}
                />
                <p className="text-xs text-gray-500 mt-1">Só os assinantes ativos veem este link, e sempre pelo link rastreado.</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Moedas aceitas</label>
                <select value={form.currencyMode} onChange={(e) => setForm({ ...form, currencyMode: e.target.value as CurrencyMode })} className={`${inputCls} field--select bg-white`}>
                  <option value="both">Euro e Real (o cliente escolhe)</option>
                  <option value="EUR">Apenas Euro (€)</option>
                  <option value="BRL">Apenas Real (R$)</option>
                </select>
              </div>
              {offeredCurrencies(form.currencyMode).includes('EUR') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço em euros (€)</label>
                  <input type="number" min="0.01" step="0.01" required value={form.prices.EUR.amount} onChange={(e) => setForm({ ...form, prices: { ...form.prices, EUR: { amount: e.target.value } } })} className={inputCls} />
                </div>
              )}
              {offeredCurrencies(form.currencyMode).includes('BRL') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preço em reais (R$)</label>
                  <input type="number" min="0.01" step="0.01" required value={form.prices.BRL.amount} onChange={(e) => setForm({ ...form, prices: { ...form.prices, BRL: { amount: e.target.value } } })} className={inputCls} />
                </div>
              )}
              {offeredCurrencies(form.currencyMode).includes('BRL') && (
                <div className="md:col-span-2 rounded-lg border border-gray-200 p-3 space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.pix.enabled}
                      onChange={(e) => setForm({ ...form, pix: { ...form.pix, enabled: e.target.checked } })}
                      className="rounded border-gray-300"
                    />
                    Aceitar Pix no real (renovação automática via Pix Automático)
                  </label>
                  {form.pix.enabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IOF de 3,5% pago por</label>
                      <select
                        value={form.pix.iofPaidBy}
                        onChange={(e) => setForm({ ...form, pix: { ...form.pix, iofPaidBy: e.target.value as PixSettings['iofPaidBy'] } })}
                        className={`${inputCls} field--select bg-white`}
                      >
                        <option value="customer">Cliente (somado ao valor no banco dele)</option>
                        <option value="office">Escritório (descontado do repasse)</option>
                      </select>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Antes de ligar, ative "Recurring payments" no Pix (Stripe → Settings → Payment methods). Sem isso o checkout em real falha.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cobrança</label>
                <select value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value as 'month' | 'year' })} className={`${inputCls} field--select bg-white`}>
                  <option value="month">Mensal</option>
                  <option value="year">Anual</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Mudar valor ou cobrança cria um preço novo na Stripe. Quem já assina continua no preço antigo.</p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 self-center">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="check-input" />
                Inscrições abertas (desmarcado: quem já paga continua entrando)
              </label>
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <Tabs
                  variant="segmented"
                  value={formLang}
                  onChange={setFormLang}
                  items={LANGS.map((l) => ({ id: l.key, label: l.label }))}
                />
                {formLang !== 'pt' && (
                  <button
                    type="button"
                    onClick={handleTranslate}
                    disabled={translating || !(form.i18n.pt.title || form.name || form.i18n.pt.description || form.i18n.pt.terms)}
                    className="link-action gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    {translating ? 'Traduzindo...' : 'Traduzir com IA'}
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título da oferta</label>
                  <input
                    value={form.i18n[formLang].title}
                    placeholder={formLang === 'pt' ? form.name : form.i18n.pt.title}
                    onChange={(e) => setForm({ ...form, i18n: { ...form.i18n, [formLang]: { ...form.i18n[formLang], title: e.target.value } } })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição (o que o membro recebe)</label>
                  <textarea
                    rows={5}
                    value={form.i18n[formLang].description}
                    onChange={(e) => setForm({ ...form, i18n: { ...form.i18n, [formLang]: { ...form.i18n[formLang], description: e.target.value } } })}
                    className={inputCls}
                  />
                  {formLang !== 'pt' && <p className="text-xs text-gray-500 mt-1">Em branco, a página usa o texto em português.</p>}
                </div>
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <label className="block text-sm font-medium text-gray-700">Termo de responsabilidade</label>
                    {formLang === 'pt' && (
                      <button type="button" onClick={handleUseTermsTemplate} className="text-sm font-medium text-primary-600 hover:text-primary-800">
                        Usar modelo padrão
                      </button>
                    )}
                  </div>
                  <textarea
                    rows={8}
                    value={form.i18n[formLang].terms}
                    onChange={(e) => setForm({ ...form, i18n: { ...form.i18n, [formLang]: { ...form.i18n[formLang], terms: e.target.value } } })}
                    className={inputCls}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Com texto, o cliente precisa aceitar o termo antes de pagar (data, IP e versão ficam registrados no membro).
                    Inclua a garantia de devolução em até 7 dias.{formLang !== 'pt' && ' Em branco, vale o termo em português.'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Variáveis (preenchidas com Configurações → Site → dados da empresa e e-mail de contato):{' '}
                    <code>{'{{empresa}}'}</code> razão social, <code>{'{{documento}}'}</code> CNPJ/NIF, <code>{'{{email}}'}</code> e-mail de contato.
                  </p>
                  <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.showCompanyDoc}
                      onChange={(e) => setForm({ ...form, showCompanyDoc: e.target.checked })}
                      className="check-input"
                    />
                    Exibir CNPJ/NIF no termo (desmarcado, {'{{documento}}'} some do texto)
                  </label>
                </div>
                {editingId && editingId !== 'new' ? (
                  <CommunityVideoEditor
                    communityId={editingId}
                    lang={formLang}
                    langLabel={LANGS.find((l) => l.key === formLang)?.label || formLang}
                    video={communities.find((c) => c._id === editingId)?.i18n?.[formLang]?.video}
                    onChanged={fetchCommunities}
                  />
                ) : (
                  <p className="text-xs text-gray-500 border-t pt-4">Salve a comunidade para adicionar o vídeo de apresentação.</p>
                )}
              </div>
            </div>

            <div className="form-actions">
              <Button variant="secondary" type="button" onClick={() => setEditingId(null)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : t('common.save')}</Button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">{t('common.loading')}</p>
          </div>
        ) : communities.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <MessagesSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Nenhuma comunidade ainda</h3>
            <p className="text-sm text-gray-500 mt-1">
              Crie uma comunidade e escolha-a no quiz da landing page. Os e-mails de nutrição passam a aceitar o placeholder <code>{'{comunidade}'}</code>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {communities.map((c) => (
              <div
                key={c._id}
                className={`bg-white rounded-lg shadow p-4 cursor-pointer ${selectedId === c._id ? 'ring-2 ring-primary-500' : ''}`}
                onClick={() => setSelectedId(c._id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{c.name}</h3>
                    <p className="text-xs text-gray-500 truncate">/comunidade/{c.slug}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${c.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {c.enabled ? 'Aberta' : 'Fechada'}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mt-2">
                  {offeredCurrencies(c.currencyMode).filter((cur) => c.prices[cur]?.amount > 0)
                    .map((cur) => formatMoney(c.prices[cur].amount, cur)).join(' · ')}
                  <span className="text-gray-500"> {c.interval === 'year' ? '/ano' : '/mês'}</span>
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {c.counts?.active || 0} ativo(s)
                  {(c.counts?.toRemove || 0) > 0 && <span className="text-red-600 font-medium"> · {c.counts?.toRemove} a remover</span>}
                </p>
                {!c.whatsappGroupUrl && <p className="text-xs text-amber-700 mt-1">Sem link do grupo configurado</p>}
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  <RowActions>
                    <ActionButton action="copyLink" value={c.offerUrl} />
                    <ActionButton action="open" label="Abrir oferta" href={c.offerUrl} />
                    <ActionButton action="edit" onClick={() => openForm(c)} />
                    {(c.counts?.total || 0) === 0 && <ActionButton action="delete" onClick={() => handleDelete(c)} />}
                  </RowActions>
                </div>
              </div>
            ))}
          </div>
        )}

        {selected && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 sm:p-6 border-b border-gray-200 space-y-3">
              <h2 className="text-lg font-semibold text-gray-900">Membros de {selected.name}</h2>
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f.key || 'all'}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={`toggle-chip px-3 py-1.5 rounded-full text-sm ${filter === f.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Nome ou e-mail" value={search} onChange={(e) => setSearch(e.target.value)} className={`${inputCls} pl-10`} />
              </div>
              {filter === 'toRemove' && (
                <p className="text-sm text-gray-600">
                  O acesso destas pessoas acabou depois de entrarem no grupo. Tire cada uma do grupo no WhatsApp e marque como removida.
                </p>
              )}
              <p className="text-xs text-gray-500">{membersTotal} membro(s)</p>
            </div>

            {membersLoading ? (
              <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div></div>
            ) : members.length === 0 ? (
              <p className="p-8 text-center text-sm text-gray-500">Nenhum membro neste filtro.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {members.map((m) => {
                  const badge = STATUS_BADGE[m.status];
                  const waDigits = m.quizLead?.whatsapp?.replace(/\D/g, '') || '';
                  return (
                    <li key={m._id} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900">{m.name || m.quizLead?.name || m.email}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                          {m.removalPendingAt && !m.removedAt && <span className="text-xs px-2 py-0.5 rounded-full bg-red-600 text-white">Remover do grupo</span>}
                          {m.removedAt && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">Removido em {formatDate(m.removedAt)}</span>}
                          {m.refundedAt && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">Estornado em {formatDate(m.refundedAt)}</span>}
                          {m.termsAcceptedAt && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Termo aceito em {formatDate(m.termsAcceptedAt)}</span>}
                        </div>
                        <p className="text-sm text-gray-600 truncate">
                          {m.email}
                          {m.quizLead?.whatsapp && <> · {formatPhone(m.quizLead.whatsapp)}</>}
                        </p>
                        <p className="text-xs text-gray-500">
                          {m.currency} · pago até {formatDate(m.paidUntil)} ·{' '}
                          {m.groupJoin?.clickedAt ? `abriu o link em ${formatDate(m.groupJoin.clickedAt)}` : 'ainda não abriu o link'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {waDigits && (
                          <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-[#25D366] text-white">
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                          </a>
                        )}
                        {(m.removalPendingAt || m.removedAt) && (
                          <Button variant="ghost" size="sm" type="button" onClick={() => toggleRemoved(m)}>
                            {m.removedAt ? <><Undo2 className="w-4 h-4" /> Desfazer</> : <><UserMinus className="w-4 h-4" /> Marcar removido</>}
                          </Button>
                        )}
                        {m.stripeCustomerId && (
                          <Button variant="ghost" size="sm" type="button" onClick={() => openPortal(m)}>
                            <ExternalLink className="w-4 h-4" /> Portal Stripe
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCommunities;
